import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  Users,
  MessageSquare,
  TrendingUp,
  TrendingDown,
  ShieldAlert,
  Coins,
  AlertTriangle,
  CheckCircle,
  type LucideIcon,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import AdminLayout from "@/components/layout/AdminLayout";
import { fetchAiHealthMetrics, AiHealthMetrics } from "@/api/aiHealthMetrics";

const TIME_RANGES = [
  { value: 7, label: "Last 7 days" },
  { value: 14, label: "Last 14 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
];

const THRESHOLDS = {
  DEPTH_GOOD: 3,
  DEPTH_LOW: 2,
  DEPTH_HEALTHY: 2.5,
  RETENTION_GOOD: 20,
  RETENTION_WARN: 10,
  RETENTION_HEALTHY: 15,
  REFUSAL_HEALTHY: 5,
  REFUSAL_DANGER: 20,
  SINGLE_EXCHANGE_MAX: 60,
};

function MetricCard({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  color = "default",
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: LucideIcon;
  trend?: { value: number; label: string };
  color?: "default" | "green" | "red" | "yellow";
}) {
  const colorClasses = {
    default: "text-primary",
    green: "text-green-600",
    red: "text-red-600",
    yellow: "text-yellow-600",
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className={`text-2xl font-bold ${colorClasses[color]}`}>
              {value}
            </p>
            {subtitle && (
              <p className="text-xs text-muted-foreground">{subtitle}</p>
            )}
          </div>
          <div className="p-2 rounded-lg bg-muted">
            <Icon className="w-5 h-5 text-muted-foreground" />
          </div>
        </div>
        {trend && (
          <div className="flex items-center gap-1 mt-2 text-xs">
            {trend.value > 0 ? (
              <TrendingUp className="w-3 h-3 text-green-600" />
            ) : trend.value < 0 ? (
              <TrendingDown className="w-3 h-3 text-red-600" />
            ) : null}
            <span
              className={
                trend.value > 0
                  ? "text-green-600"
                  : trend.value < 0
                    ? "text-red-600"
                    : "text-muted-foreground"
              }
            >
              {trend.label}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function HealthCheck({
  label,
  ok,
  detail,
}: {
  label: string;
  ok: boolean;
  detail: string;
}) {
  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center gap-2">
        {ok ? (
          <CheckCircle className="w-4 h-4 text-green-600" />
        ) : (
          <AlertTriangle className="w-4 h-4 text-yellow-600" />
        )}
        <span className="text-sm font-medium">{label}</span>
      </div>
      <span className="text-sm text-muted-foreground">{detail}</span>
    </div>
  );
}

function calcTrend(
  current: number | null | undefined,
  prev: number | null | undefined
): { value: number; label: string } | undefined {
  if (current == null || prev == null || prev === 0) return undefined;
  const pct = Math.round(((current - prev) / prev) * 100);
  if (pct === 0) return { value: 0, label: "No change vs prev period" };
  return {
    value: pct,
    label: `${pct > 0 ? "+" : ""}${pct}% vs prev period`,
  };
}

export default function AiHealthDashboard() {
  const [days, setDays] = useState(7);

  const { data: metrics, isLoading } = useQuery({
    queryKey: ["ai-health-metrics", days],
    queryFn: () => fetchAiHealthMetrics(days),
    staleTime: 5 * 60 * 1000,
  });

  const engagement = metrics?.engagement;
  const engagementPrev = metrics?.engagement_prev;
  const retention = metrics?.retention;
  const refusal = metrics?.refusal;
  const tokenUsage = metrics?.token_usage;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">AI Health Dashboard</h1>
            <p className="text-muted-foreground">
              Monitor AI quality, engagement, and system health
            </p>
          </div>
          <div className="flex gap-2">
            {TIME_RANGES.map((range) => (
              <button
                key={range.value}
                onClick={() => setDays(range.value)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  days === range.value
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            Loading metrics...
          </div>
        ) : metrics ? (
          <>
            {/* Row 1: Key Metric Cards */}
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
              <MetricCard
                title="Sessions"
                value={engagement?.total_sessions ?? 0}
                icon={MessageSquare}
                trend={calcTrend(engagement?.total_sessions, engagementPrev?.total_sessions)}
              />
              <MetricCard
                title="Users"
                value={engagement?.total_users ?? 0}
                icon={Users}
                trend={calcTrend(engagement?.total_users, engagementPrev?.total_users)}
              />
              <MetricCard
                title="Avg Depth"
                value={engagement?.avg_messages_per_session ?? 0}
                subtitle="messages/session"
                icon={Activity}
                color={
                  (engagement?.avg_messages_per_session ?? 0) >= THRESHOLDS.DEPTH_GOOD
                    ? "green"
                    : (engagement?.avg_messages_per_session ?? 0) < THRESHOLDS.DEPTH_LOW
                      ? "red"
                      : "default"
                }
                trend={calcTrend(
                  engagement?.avg_messages_per_session,
                  engagementPrev?.avg_messages_per_session
                )}
              />
              <MetricCard
                title="Retention"
                value={`${retention?.retention_pct ?? 0}%`}
                subtitle={`${retention?.returning_users ?? 0} / ${retention?.total_users ?? 0} users`}
                icon={TrendingUp}
                color={
                  (retention?.retention_pct ?? 0) >= THRESHOLDS.RETENTION_GOOD
                    ? "green"
                    : (retention?.retention_pct ?? 0) < THRESHOLDS.RETENTION_WARN
                      ? "red"
                      : "yellow"
                }
              />
              <MetricCard
                title="Refusal Rate"
                value={`${refusal?.refusal_pct ?? 0}%`}
                subtitle={`${refusal?.refusal_count ?? 0} / ${refusal?.total_responses ?? 0}`}
                icon={ShieldAlert}
                color={
                  (refusal?.refusal_pct ?? 0) <= THRESHOLDS.REFUSAL_HEALTHY
                    ? "green"
                    : (refusal?.refusal_pct ?? 0) > THRESHOLDS.REFUSAL_DANGER
                      ? "red"
                      : "yellow"
                }
              />
              <MetricCard
                title="Total Tokens"
                value={
                  tokenUsage?.total_tokens
                    ? `${(tokenUsage.total_tokens / 1000).toFixed(1)}K`
                    : "0"
                }
                subtitle={`${tokenUsage?.total_credits_used?.toLocaleString() ?? 0} credits`}
                icon={Coins}
              />
            </div>

            {/* Row 2: Daily Trend Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Daily Trend</CardTitle>
                <CardDescription>
                  Sessions, users, and engagement depth over time
                </CardDescription>
              </CardHeader>
              <CardContent>
                {metrics.daily_trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={metrics.daily_trend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="day"
                        tickFormatter={(d) =>
                          new Date(d).toLocaleDateString("en", {
                            month: "short",
                            day: "numeric",
                          })
                        }
                        fontSize={12}
                      />
                      <YAxis yAxisId="left" fontSize={12} />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        fontSize={12}
                      />
                      <Tooltip
                        labelFormatter={(d) =>
                          new Date(d).toLocaleDateString("en", {
                            weekday: "short",
                            month: "short",
                            day: "numeric",
                          })
                        }
                      />
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="sessions"
                        stroke="#3b82f6"
                        fill="#3b82f680"
                        name="Sessions"
                      />
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="users"
                        stroke="#10b981"
                        fill="#10b98140"
                        name="Users"
                      />
                      <Area
                        yAxisId="right"
                        type="monotone"
                        dataKey="avg_depth"
                        stroke="#f59e0b"
                        fill="transparent"
                        strokeDasharray="5 5"
                        name="Avg Depth"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-48 text-muted-foreground">
                    No data for this period
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Row 3: Persona Performance (full width) */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Persona Performance</CardTitle>
                <CardDescription>
                  Engagement depth, satisfaction, and retention per persona
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Persona</TableHead>
                      <TableHead className="text-right">Sessions</TableHead>
                      <TableHead className="text-right">Users</TableHead>
                      <TableHead className="text-right">Avg Depth</TableHead>
                      <TableHead className="text-right">Deep (6+)</TableHead>
                      <TableHead className="text-right">1-Exchange %</TableHead>
                      <TableHead className="text-right">Satisfaction</TableHead>
                      <TableHead className="text-right">Retention</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(metrics.persona_performance || []).length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={8}
                          className="text-center text-muted-foreground"
                        >
                          No sessions in this period
                        </TableCell>
                      </TableRow>
                    ) : (
                      metrics.persona_performance.map((p) => (
                        <TableRow key={p.persona}>
                          <TableCell className="font-medium">
                            <Badge variant="secondary">{p.persona}</Badge>
                          </TableCell>
                          <TableCell className="text-right">{p.sessions}</TableCell>
                          <TableCell className="text-right">{p.users}</TableCell>
                          <TableCell className="text-right">
                            <span className={
                              p.avg_depth >= THRESHOLDS.DEPTH_GOOD ? "text-green-600 font-medium" :
                              p.avg_depth < THRESHOLDS.DEPTH_LOW ? "text-red-600" : ""
                            }>
                              {p.avg_depth}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">{p.deep_sessions}</TableCell>
                          <TableCell className="text-right">
                            <span className={
                              p.single_exchange_pct < THRESHOLDS.SINGLE_EXCHANGE_MAX ? "text-green-600" : "text-red-600"
                            }>
                              {p.single_exchange_pct}%
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            {p.satisfaction_pct != null ? (
                              <span className={p.satisfaction_pct >= 80 ? "text-green-600 font-medium" : p.satisfaction_pct < 50 ? "text-red-600" : ""}>
                                {p.satisfaction_pct}%
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {p.retention_pct != null ? (
                              <span className={p.retention_pct >= THRESHOLDS.RETENTION_HEALTHY ? "text-green-600 font-medium" : ""}>
                                {p.retention_pct}%
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Row 4: Top Books */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {/* Top Books in Chat */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Top Books in Chat</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Book</TableHead>
                        <TableHead className="text-right">Sessions</TableHead>
                        <TableHead className="text-right">Messages</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {metrics.top_books.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={3}
                            className="text-center text-muted-foreground"
                          >
                            No book chats in this period
                          </TableCell>
                        </TableRow>
                      ) : (
                        metrics.top_books.map((b) => (
                          <TableRow key={b.book_id}>
                            <TableCell className="font-medium">
                              {b.book_title}
                            </TableCell>
                            <TableCell className="text-right">
                              {b.sessions}
                            </TableCell>
                            <TableCell className="text-right">
                              {b.total_messages}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>

            {/* Row 4: Token Usage & Health Checks */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {/* Token Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Token Usage</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[
                    { label: "User Tokens (input)", value: tokenUsage?.total_user_tokens, bold: false },
                    { label: "Assistant Tokens (output)", value: tokenUsage?.total_assistant_tokens, bold: false },
                    { label: "Total Tokens", value: tokenUsage?.total_tokens, bold: true },
                    { label: "Avg Tokens / Session", value: tokenUsage?.avg_tokens_per_session, bold: false },
                    { label: "Total Credits Used", value: tokenUsage?.total_credits_used, bold: false },
                  ].map((row, i, arr) => (
                    <div key={row.label} className={`flex justify-between py-2 ${i < arr.length - 1 ? "border-b" : ""}`}>
                      <span className="text-sm text-muted-foreground">{row.label}</span>
                      <span className={row.bold ? "font-bold" : "font-medium"}>
                        {(row.value ?? 0).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Health Checks */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Health Checks</CardTitle>
                </CardHeader>
                <CardContent>
                  <HealthCheck
                    label="Prompt Leaking"
                    ok={(metrics.prompt_leaking?.suspected_leaks ?? 0) === 0}
                    detail={
                      (metrics.prompt_leaking?.suspected_leaks ?? 0) === 0
                        ? "No leaks detected"
                        : `${metrics.prompt_leaking.suspected_leaks} suspected leak(s)`
                    }
                  />
                  <HealthCheck
                    label="Refusal Rate"
                    ok={(refusal?.refusal_pct ?? 0) <= THRESHOLDS.REFUSAL_HEALTHY}
                    detail={
                      (refusal?.refusal_pct ?? 0) <= THRESHOLDS.REFUSAL_HEALTHY
                        ? `${refusal?.refusal_pct ?? 0}% — healthy`
                        : `${refusal?.refusal_pct ?? 0}% — too high`
                    }
                  />
                  <HealthCheck
                    label="Session Depth"
                    ok={(engagement?.avg_messages_per_session ?? 0) >= THRESHOLDS.DEPTH_HEALTHY}
                    detail={`${engagement?.avg_messages_per_session ?? 0} avg messages/session`}
                  />
                  <HealthCheck
                    label="Single-Exchange Rate"
                    ok={(engagement?.single_exchange_pct ?? 100) < THRESHOLDS.SINGLE_EXCHANGE_MAX}
                    detail={`${engagement?.single_exchange_pct ?? 0}% sessions are 1 exchange only`}
                  />
                  <HealthCheck
                    label="User Retention"
                    ok={(retention?.retention_pct ?? 0) >= THRESHOLDS.RETENTION_HEALTHY}
                    detail={`${retention?.retention_pct ?? 0}% of users return`}
                  />
                </CardContent>
              </Card>
            </div>
          </>
        ) : null}
      </div>
    </AdminLayout>
  );
}
